import { Alert, Button, Card, Space, Table, Tag, Typography } from 'antd';
import { CopyOutlined, PlusOutlined } from '@ant-design/icons';
import { MockBanner } from '../../../pages/components/MockBanner';
import { PageHeader } from '../../../pages/components/PageHeader';

/** Admin screen 26. Runtime editor. Future instalments only; issued never rewritten (M-01). */
export function FeeStructuresEditorPage() {
  return (
    <div>
      <MockBanner when="the fee-structure module" />
      <PageHeader
        title="Fee structure · 2026–27"
        subtitle="6 heads · 3 instalments · applied to 2,600 students · locked once invoiced (M-01)"
        actions={
          <>
            <Button icon={<CopyOutlined />} disabled>Duplicate for next year</Button>
            <Button type="primary" icon={<PlusOutlined />} disabled>Add a head</Button>
          </>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Structure changes re-derive future instalments only"
        description={<Typography.Text style={{ fontSize: 12.5 }}>Issued invoices are never rewritten. A mid-year fee change needs a new structure with a reason and an approver.</Typography.Text>}
      />

      <Card size="small" title="Fee heads" style={{ marginBottom: 12 }}>
        <Table
          size="small"
          pagination={false}
          rowKey="head"
          dataSource={HEADS}
          columns={[
            { title: 'Head', dataIndex: 'head', key: 'head', render: (v: string) => <span style={{ fontWeight: 500 }}>{v}</span> },
            { title: 'Applies to', dataIndex: 'applies', key: 'applies', render: (v: string) => <Typography.Text type="secondary" style={{ fontSize: 12 }}>{v}</Typography.Text> },
            { title: 'Class 1–5', dataIndex: 'g1', key: 'g1', width: 90, align: 'right', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span> },
            { title: 'Class 6–8', dataIndex: 'g2', key: 'g2', width: 90, align: 'right', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span> },
            { title: 'Class 9–10', dataIndex: 'g3', key: 'g3', width: 90, align: 'right', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span> },
            { title: 'Inst.', dataIndex: 'inst', key: 'inst', width: 60, align: 'center' },
            { key: 'edit', width: 60, align: 'right', render: () => <Button size="small" type="text" disabled>Edit</Button> },
          ]}
        />
        <div style={{ padding: '10px 0 0', borderTop: '1px solid #f1f1f4', marginTop: 10 }}>
          <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>
            A Class 9 student on transport pays <b style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>₹1,01,000</b>/year.
          </Typography.Text>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
        <Card size="small" title="Instalments">
          <InstRow label="First" date="15 Apr" pct="40%" />
          <InstRow label="Second" date="15 Sep" pct="30%" />
          <InstRow label="Third" date="15 Dec" pct="30%" last />
          <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11.5, marginTop: 10 }}>
            Late fee: ₹50/day after 7 days, capped at ₹1,000 per instalment.
          </Typography.Text>
        </Card>

        <Card size="small" title="Concessions">
          <ConcRow name="Second sibling" amount="−10% tuition" rule="Auto-applied when a family already has a paying child" />
          <ConcRow name="Staff ward" amount="−50%" rule="Any full-time staff member" />
          <ConcRow name="Merit" amount="−25%" rule="Top 3 in previous exam · reviewed each term" />
          <ConcRow name="Hardship" amount="Case by case" rule="Requires principal signature + reason (M-02)" last />
        </Card>
      </div>
    </div>
  );
}

function InstRow({ label, date, pct, last }: { label: string; date: string; pct: string; last?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: last ? 'none' : '1px solid #f1f1f4' }}>
      <div>
        <div style={{ fontWeight: 500, fontSize: 13 }}>{label}</div>
        <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>{date}</Typography.Text>
      </div>
      <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontWeight: 600 }}>{pct}</div>
    </div>
  );
}

function ConcRow({ name, amount, rule, last }: { name: string; amount: string; rule: string; last?: boolean }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 10, padding: '8px 0', borderBottom: last ? 'none' : '1px solid #f1f1f4' }}>
      <div>
        <div style={{ fontWeight: 500, fontSize: 13 }}>{name}</div>
        <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>{rule}</Typography.Text>
      </div>
      <div style={{ textAlign: 'right' }}>
        <Tag>{amount}</Tag>
      </div>
    </div>
  );
}

const HEADS = [
  { head: 'Tuition', applies: 'annual · required', g1: '₹42,000', g2: '₹56,000', g3: '₹72,000', inst: 3 },
  { head: 'Admission', applies: 'once · at joining', g1: '₹15,000', g2: '₹15,000', g3: '₹15,000', inst: 1 },
  { head: 'Transport', applies: 'if on a route', g1: '₹18,000', g2: '₹18,000', g3: '₹18,000', inst: 3 },
  { head: 'Lab', applies: 'science stream', g1: '—', g2: '₹4,000', g3: '₹6,000', inst: 1 },
  { head: 'Library', applies: 'annual · required', g1: '₹2,500', g2: '₹3,500', g3: '—', inst: 1 },
  { head: 'Activities', applies: 'per term', g1: '₹3,000', g2: '₹4,000', g3: '₹5,000', inst: 3 },
];
